import React, { useState, useEffect } from 'react';
import './ResultsDashboard.css';

const ResultsDashboard = ({ organizerId }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    loadSessions();
  }, [organizerId]);

  const loadDetailedReport = async (resultId) => {
    try {
      const response = await fetch(
        `/api/organizer/results/${resultId}/detailed-report`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedReport(data);
        console.log("Loaded detailed report:", data);
        setShowReport(true);
      }
    } catch (err) {
      console.error("Failed to load detailed report", err);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await fetch(`/api/organizer/${organizerId}/sessions`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadResults = async (sessionId) => {
    try {
      const response = await fetch(`/api/organizer/sessions/${sessionId}/results`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        console.log('Loaded results:', data.results);
      }
    } catch (err) {
      console.error('Failed to load results:', err);
    }
  };

  const handleSessionSelect = (sessionId) => {
    setSelectedSession(sessionId);
    loadResults(sessionId);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="results-dashboard">
      <h2>Results Dashboard</h2>
      <p className="subtitle">View and analyze screening results</p>

      <div className="results-content">
        <div className="sessions-sidebar">
          <h3>Select Session</h3>
          {sessions.length === 0 ? (
            <p className="empty">No sessions available</p>
          ) : (
            <div className="sessions-list">
              {sessions.map((session) => (
                <div
                  key={session.session_id}
                  className={`session-item ${
                    selectedSession === session.session_id ? 'active' : ''
                  }`}
                  onClick={() => handleSessionSelect(session.session_id)}
                >
                  <div className="session-item-title">{session.job_title || 'Untitled'}</div>
                  <div className="session-item-meta">
                    {session.response_count || 0} responses
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="results-main">
          {selectedSession ? (
            <>
              <h3>Results</h3>
              {results.length === 0 ? (
                <div className="empty-state">
                  <p>No responses yet for this session.</p>
                </div>
              ) : (
                <div className="results-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Participant</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Submitted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((result, index) => (
                        <tr key={index}>
                          <td>{result.participant_name || 'N/A'}</td>
                          <td>{result.participant_email}</td>
                          <td>
                            <span className={`status-badge status-${result.status}`}>
                              {result.status}
                            </span>
                          </td>
                          <td>
                            {result.final_score?.Score || result.auto_score?.Score || 'N/A'}
                          </td>
                          <td>
                            {result.submitted_at
                              ? new Date(result.submitted_at).toLocaleString()
                              : 'N/A'}
                          </td>
                          <td>
                            <button
                              className="rd-view-report-btn"
                              onClick={() => loadDetailedReport(result.result_id)}
                            >
                              View Report
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <p>Select a session to view results</p>
            </div>
          )}
        </div>
      </div>
      {showReport && selectedReport && (
        <div className="rd-report-overlay">
          <div className="rd-report-modal">

            {/* Header */}
            <div className="rd-report-header">
              <div>
                <h2>Startup Evaluation Report</h2>
                <p className="rd-subtitle">Investor-style readiness assessment</p>
              </div>
              <button className="rd-close-btn" onClick={() => setShowReport(false)}>
                ✕
              </button>
            </div>

            {/* Primary Score */}
            <div className="rd-hero-score">
              <div className="rd-hero-card">
                <span>Investment Readiness Score</span>
                <h1>{selectedReport.investment_readiness_score}</h1>
                <p className="rd-reco">{selectedReport.recommendation}</p>
              </div>
            </div>

            {/* Metrics */}
            <div className="rd-metrics-grid">
              {Object.entries(selectedReport.metrics || {}).map(([k, v]) => (
                <div key={k} className="rd-metric-card">
                  <span>{k.toUpperCase()}</span>
                  <h3>{v}</h3>
                </div>
              ))}
            </div>

            {/* Narrative */}
            <div className="rd-two-col">
              <div className="rd-card success">
                <h4>Strengths</h4>
                <ul className="rd-narrative">
                  {selectedReport.strengths?.split('.').filter(Boolean).map((s, i) => (
                    <li key={i}>{s.trim()}.</li>
                  ))}
                </ul>
              </div>

              <div className="rd-card warning">
                <h4>Weaknesses</h4>
                <ul className="rd-narrative" style={{ background: "#fff3cd" }}>
                  {selectedReport.weaknesses?.split('.').filter(Boolean).map((w, i) => (
                    <li key={i}>{w.trim()}.</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rd-card neutral">
              <h4>Key Highlights</h4>
              <ul className="rd-narrative" style={{ background: "#f1f5f9" }}>
                {selectedReport.highlights?.split('.').filter(Boolean).map((h, i) => (
                  <li key={i}>{h.trim()}.</li>
                ))}
              </ul>
            </div>

            {/* Red Flags */}
            <div className="rd-card danger">
              <h4>Red Flags 🚩</h4>
              {selectedReport.red_flags?.length ? (
                <ul>
                  {selectedReport.red_flags.map((r, i) => (
                    <li key={i}>
                      <strong>{r.type}</strong> ({r.severity}) — {r.reason}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No material red flags identified.</p>
              )}
            </div>

            <div className="rd-card neutral">
              <h4>Interview Conversation</h4>

              <div className="rd-conversation">
                {selectedReport.conversation?.map((msg, i) => (
                  <div
                    key={i}
                    className={`rd-chat-bubble ${msg.role === "assistant" ? "ai" : "user"}`}
                  >
                    <span className="rd-chat-role">
                      {msg.role === "assistant" ? "Interviewer" : "Founder"}
                    </span>
                    <p>{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ResultsDashboard;

